import { getAllStores, IStore } from "@/api/store.ts";
import {
    Autocomplete,
    Card,
    CardContent,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { PricingScope } from "../pricingScope";

interface Props {
    scope: PricingScope;
    onChange: (scope: PricingScope) => void;
}

const GLOBAL_OPTION: IStore = {
    id: -1,
    name: "Global Defaults",
} as IStore;

/**
 * Chooses whether pricing edits apply globally or to one store.
 *
 * Searches SERVER-SIDE rather than fetching one page and filtering it here. The store
 * list is paginated, so a single fetch caps how many stores are selectable at the page
 * size — and the store past that cap is not merely missing from the list, it is
 * unreachable, with no way for an admin to price it at all.
 *
 * Debounce and filter shape follow StoreAutoComplete, which solves the same problem for
 * the user-accounts form.
 */
export const PricingScopeSelector = ({ scope, onChange }: Props) => {
    const [searchValue, setSearchValue] = useState("");
    const [debounced, setDebounced] = useState("");

    useEffect(() => {
        const handler = setTimeout(() => setDebounced(searchValue), 500);
        return () => clearTimeout(handler);
    }, [searchValue]);

    const { data, isPending } = useQuery({
        // The search term is part of the key, so each term caches separately and a
        // stale response cannot paint over the results for a newer one.
        queryKey: ["pricing-scope-stores", debounced],
        // buildQuery drops an empty q, so the untouched field asks for the first page
        // unfiltered rather than searching for "".
        queryFn: () =>
            getAllStores(1, 50, { q: debounced.trim(), sort_by: "name" }),
    });

    const stores = data?.items ?? [];
    const selected =
        scope.storeId === null
            ? GLOBAL_OPTION
            : (stores.find((store) => store.id === scope.storeId) ??
              ({ id: scope.storeId, name: scope.storeName ?? "" } as IStore));

    return (
        <Card>
            <CardContent>
                <Stack spacing={1}>
                    <Typography variant="h6" fontWeight={700}>
                        Pricing Scope
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Global Defaults sets base pricing for every store.
                        Choose a store to override individual values for that
                        store only — anything you do not override keeps
                        following the global sheet.
                    </Typography>
                    <Autocomplete
                        options={[GLOBAL_OPTION, ...stores]}
                        loading={isPending}
                        value={selected}
                        // The server already applied the search. Filtering the response
                        // again here would hide any store it matched on a field this
                        // component does not render.
                        filterOptions={(options) => options}
                        onInputChange={(_event, value, reason) => {
                            // Only real typing. Selecting a store also fires this, with
                            // the chosen name as the value, which would search for it
                            // and refetch on every selection.
                            if (reason === "input") setSearchValue(value);
                        }}
                        isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                        }
                        getOptionLabel={(option) => option.name}
                        onChange={(_event, option) => {
                            if (!option || option.id === GLOBAL_OPTION.id) {
                                onChange({ storeId: null, storeName: null });
                                return;
                            }
                            onChange({
                                storeId: option.id,
                                storeName: option.name,
                            });
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Editing pricing for"
                            />
                        )}
                    />
                </Stack>
            </CardContent>
        </Card>
    );
};
